import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import PublicNavbar from './components/layout/PublicNavbar';
import Sidebar from './components/layout/Sidebar';
import Footer from './components/layout/Footer';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Support from './pages/Support';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Membership from './pages/Membership';
import UpgradePage from './pages/UpgradePage';
import BillingHistory from './pages/BillingHistory';
import BillingSettings from './pages/BillingSettings';
import CancelSubscription from './pages/CancelSubscription';
import PasswordReset from './pages/PasswordReset';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import GoogleCallback from './pages/GoogleCallback';
import { getUser } from './services/auth';
import { getOnboardingStatus } from './services/api';
import { safeRedirectPath } from './utils/redirect';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import MyRegistrations from './pages/MyRegistrations';
import AssociateMembers from './pages/AssociateMembers';
import Onboarding from './pages/Onboarding';
// Admin pages (scaffold)
import AdminEventsList from './pages/admin/AdminEventsList';
import AdminEventForm from './pages/admin/AdminEventForm';
import AdminEventRegistrations from './pages/admin/AdminEventRegistrations';
import AdminBillingHistory from './pages/admin/AdminBillingHistory';
import AdminMembersList from './pages/admin/AdminMembersList';
import AdminMemberEdit from './pages/admin/AdminMemberEdit';

function App() {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const setUser = setUserState;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getUser();
        setUserState(userData);
      } catch (error) {
        setUserState(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchOnboarding = async () => {
      if (!user) {
        console.log('📋 App: No user, clearing onboarding status');
        setOnboardingStatus(null);
        return;
      }

      console.log('📋 App: Fetching onboarding status for user:', user.email);
      setOnboardingLoading(true);
      try {
        const response = await getOnboardingStatus();
        if (!cancelled) {
          const membershipData = response.membership || response;
          console.log('📋 App: Received onboarding status:', {
            onboarding_status: membershipData?.onboarding_status,
            status: membershipData?.status,
            membership_type: membershipData?.membership_type?.name
          });
          setOnboardingStatus(membershipData);
        }
      } catch (err) {
        console.warn('📋 App: Unable to fetch onboarding status:', err);
        if (!cancelled) {
          setOnboardingStatus(null);
        }
      } finally {
        if (!cancelled) {
          setOnboardingLoading(false);
        }
      }
    };

    fetchOnboarding();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Protected route wrapper
  const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    console.log('🛡️ ProtectedRoute check:', {
      location: location.pathname,
      loading: loading,
      onboardingLoading: onboardingLoading,
      onboardingStatus: onboardingStatus?.onboarding_status,
      user: user?.email
    });

    if (loading || onboardingLoading) {
      return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }
    if (user) {
      if (
        onboardingStatus &&
        onboardingStatus.onboarding_status &&
        onboardingStatus.onboarding_status !== 'completed' &&
        location.pathname !== '/onboarding'
      ) {
        console.log('🔄 ProtectedRoute redirecting to /onboarding - onboarding not completed');
        // Preserve the intended destination through onboarding
        const redirectParam = location.pathname !== '/dashboard'
          ? `?redirect=${encodeURIComponent(location.pathname)}`
          : '';
        return <Navigate to={`/onboarding${redirectParam}`} replace />;
      }
      console.log('✅ ProtectedRoute showing content - onboarding completed or on onboarding page');
      return children;
    } else {
      console.log('🔐 ProtectedRoute redirecting to login - no user');
      // Pass the intended destination as a redirect parameter
      const redirectParam = location.pathname !== '/login'
        ? `?redirect=${encodeURIComponent(location.pathname)}`
        : '';
      return <Navigate to={`/login${redirectParam}`} replace />;
    }
  };

  // Public route wrapper (redirect away if already logged in, honoring ?redirect= param)
  const PublicRoute = ({ children }) => {
    const location = useLocation();
    if (loading) {
      return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }
    if (!user) {
      return children;
    }
    const params = new URLSearchParams(location.search);
    const redirectTo = safeRedirectPath(params.get('redirect'));
    return <Navigate to={redirectTo} replace />;
  };

  
  // Layout component for authenticated app pages
  const AppLayout = ({ user, children }) => {
    // Logged-out visitors (e.g. browsing public events) get the public top nav
    // and no member sidebar, matching other public pages. The fixed PublicNavbar
    // needs top padding so content isn't hidden underneath it.
    if (!user) {
      return (
        <div className="flex flex-col min-h-screen bg-gray-50">
          <PublicNavbar />
          <main className="flex-1 p-4 pt-20 overflow-auto">
            {children}
          </main>
          <Footer />
        </div>
      );
    }
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Navbar user={user} />
        <div className="flex flex-1">
          <Sidebar user={user} />
          <main className="flex-1 p-4 overflow-auto">
            {children}
          </main>
        </div>
        <Footer />
      </div>
    );
  };

  // Staff-only route wrapper
  const StaffRoute = ({ children }) => {
    if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
    if (user && (user.is_staff || user.is_superuser)) return children;
    return <Navigate to="/dashboard" replace />;
  };

  return (
    <Router>
      <Routes>
        {/* Public home page */}
        <Route path="/" element={<Home />} />
        
        {/* Public auth routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login setUser={setUser} />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register setUser={setUser} />
            </PublicRoute>
          } 
        />
        <Route 
          path="/password-reset" 
          element={
            <PublicRoute>
              <PasswordReset />
            </PublicRoute>
          } 
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <PasswordResetConfirm />
            </PublicRoute>
          }
        />
        
        {/* Google OAuth callback */}
        <Route 
          path="/auth/google/callback" 
          element={
            <PublicRoute>
              <GoogleCallback setUser={setUser} />
            </PublicRoute>
          } 
        />
        
        {/* Protected app routes with layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <Dashboard user={user} />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <Profile user={user} setUser={setUser} />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <Support />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding onStatusChange={setOnboardingStatus} />
            </ProtectedRoute>
          }
        />
        
        {/* Additional protected routes can be added here */}
        <Route 
          path="/membership" 
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <Membership />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/upgrade" 
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <UpgradePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing-history"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <BillingHistory />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing-settings"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <BillingSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cancel-subscription"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <CancelSubscription />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <AppLayout user={user}>
              <Events user={user} />
            </AppLayout>
          }
        />
        <Route
          path="/events/:id"
          element={
            <AppLayout user={user}>
              <EventDetail user={user} />
            </AppLayout>
          }
        />
        <Route
          path="/my-registrations"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <MyRegistrations user={user} />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/associate-members"
          element={
            <ProtectedRoute>
              <AppLayout user={user}>
                <AssociateMembers user={user} />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin: Events management */}
        <Route 
          path="/admin/events" 
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminEventsList />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/events/new" 
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminEventForm mode="create" />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/events/:id" 
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminEventForm mode="edit" />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/admin/events/:id/registrations"
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminEventRegistrations />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminBillingHistory />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin: Member management */}
        <Route
          path="/admin/members"
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminMembersList />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members/:id"
          element={
            <ProtectedRoute>
              <StaffRoute>
                <AppLayout user={user}>
                  <AdminMemberEdit />
                </AppLayout>
              </StaffRoute>
            </ProtectedRoute>
          }
        />

        {/* 404 Page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
